<template>
  <dialog
    ref="dialogRef"
    :class="classes.Root"
    :aria-label="props.ariaLabel || t('design_system.light_box.label')"
    aria-modal="true"
    closedby="closerequest"
    data-cy="ds-light-box"
    @cancel.prevent="onCancel"
    @keydown="onKeydown"
    @close="onNativeClose"
  >
    <template v-if="open">
      <span
        :class="classes.FocusBoundary"
        tabindex="0"
        aria-hidden="true"
        data-cy="ds-light-box__focus-start"
        @focus="onStartBoundaryFocus"
      />
      <header :class="classes.Header">
        <h2
          :class="classes.Title"
          data-cy="ds-light-box__title"
        >
          {{ item?.name }}
        </h2>
        <div
          v-if="item && hasSlotContent(slots, 'actions', { item, activeIndex: index })"
          :key="itemKey"
          :class="classes.Actions"
          role="group"
          :aria-label="t('design_system.light_box.actions')"
          data-cy="ds-light-box__actions"
        >
          <slot
            name="actions"
            :item="item"
            :active-index="index"
          />
        </div>
        <div :class="classes.Close">
          <DSButton
            variant="ghost"
            size="lg"
            prefix-icon="cross"
            :aria-label="t('button_close')"
            autofocus
            data-cy="ds-light-box__close"
            @click="onCloseClick"
          />
        </div>
      </header>
      <main
        :class="[classes.Viewport, item && item.type !== 'image' && classes.PlayerViewport]"
        data-cy="ds-light-box__viewport"
        @pointerdown="onPointerDown"
        @pointerup="onPointerUp"
        @pointercancel="onPointerCancel"
      >
        <div
          v-if="item"
          :key="itemKey"
          :class="classes.Content"
        >
          <slot
            :item="item"
            :active-index="index"
          >
            <DSLightBoxMedia :item="item" />
          </slot>
        </div>
        <p
          v-else
          role="status"
          data-cy="ds-light-box__empty"
        >
          {{ t("label_no_data") }}
        </p>
        <DSButton
          v-if="props.items.length > 1"
          :class="classes.Previous"
          variant="tertiary"
          size="lg"
          prefix-icon="arrow-left"
          :aria-label="t('label_previous')"
          :disabled="index <= 0"
          data-cy="ds-light-box__previous"
          @click="onPreviousClick"
        />
        <DSButton
          v-if="props.items.length > 1"
          :class="classes.Next"
          variant="tertiary"
          size="lg"
          prefix-icon="arrow-right"
          :aria-label="t('label_next')"
          :disabled="index >= props.items.length - 1"
          data-cy="ds-light-box__next"
          @click="onNextClick"
        />
        <DSLightBoxDescription
          v-if="item?.description?.trim()"
          :key="itemKey"
          :description="item.description"
        />
      </main>
      <span
        :class="classes.FocusBoundary"
        tabindex="0"
        aria-hidden="true"
        data-cy="ds-light-box__focus-end"
        @focus="onEndBoundaryFocus"
      />
    </template>
  </dialog>
</template>

<script setup lang="ts">
import { computed, useTemplateRef, watch } from "vue";
import DSButton from "@/src/design_system/button_and_controls/button/components/DSButton.vue";
import { useLightBoxDialog } from "../composables/useLightBoxDialog";
import { useLightBoxNavigation } from "../composables/useLightBoxNavigation";
import DSLightBoxMedia from "./DSLightBoxMedia.vue";
import DSLightBoxDescription from "./DSLightBoxDescription.vue";
import { DS_LIGHT_BOX_CLASSES as classes } from "../constants/classes";
import type { DSLightBoxProps } from "../types/props";
import type { DSLightBoxSlots } from "../types/slots";
import { hasSlotContent } from "@/src/shared/utils/slots";

const props = withDefaults(defineProps<DSLightBoxProps>(), {
  items: () => [],
  ariaLabel: undefined,
});
const open = defineModel<boolean>("open", { default: false });
const activeIndex = defineModel<number>("activeIndex", { default: 0 });
const slots = defineSlots<DSLightBoxSlots>();
const t = window.EasyLocale.getLocale;
const dialogRef = useTemplateRef<HTMLDialogElement>("dialogRef");
const { onStartBoundaryFocus, onEndBoundaryFocus, restoreRemovedFocus } = useLightBoxDialog(open, dialogRef);
const clamp = (value: number) =>
  Math.max(0, Math.min(Number.isFinite(value) ? Math.trunc(value) : 0, props.items.length - 1));
const index = computed(() => clamp(activeIndex.value));
const item = computed(() => props.items[index.value]);
const itemKey = computed(() => JSON.stringify([index.value, item.value?.id, item.value?.src, item.value?.type]));

watch(
  [activeIndex, () => props.items.length],
  () => {
    if (activeIndex.value !== index.value) activeIndex.value = index.value;
  },
  { immediate: true }
);
watch(itemKey, restoreRemovedFocus);

const close = () => void (open.value = false);
const onNativeClose = () => {
  if (!dialogRef.value?.open) close();
};
const show = (value = activeIndex.value) => {
  activeIndex.value = clamp(value);
  open.value = true;
};
const next = () => void (activeIndex.value = clamp(index.value + 1));
const previous = () => void (activeIndex.value = clamp(index.value - 1));
const { onKeydown, onPointerDown, onPointerUp, onPointerCancel } = useLightBoxNavigation({
  itemKey,
  next,
  previous,
});
const onCloseClick = () => close();
const onNextClick = () => next();
const onPreviousClick = () => previous();
const onCancel = () => close();
defineExpose({ show, close, next, previous });
</script>
